// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.

import { err, ok, type Result } from "neverthrow";
import { proxyErrorMessage } from "~/lib/error-handling";

export type MemoryScope = "user" | "project" | "organization";

export interface MemoryEntry {
  id: string;
  scope: MemoryScope;
  content: string;
  pinned: boolean;
  pinnedScope?: string;
  createdBy: string;
  createdAt: string;
  modifiedAt: string;
}

export interface PaginatedList<T> {
  data: T[];
  firstId?: string;
  lastId?: string;
  hasMore: boolean;
}

export type HealthState = "healthy" | "fading" | "stale" | "dormant";

export interface MemoryHealthEntry {
  id: string;
  scope: MemoryScope;
  content: string;
  category: string;
  importance: number;
  pinned: boolean;
  healthScore: number;
  healthState: HealthState;
  lastAccessedAt: string | null;
  accessCount: number;
  createdAt: string;
}

export interface CreateMemoryInput {
  scope: MemoryScope;
  content: string;
}

export interface UpdateMemoryInput {
  content: string;
}

export interface CompressConfirmInput {
  originalIds: string[];
  compressedText: string;
  category: string;
  importance: number;
  scope: MemoryScope;
}

export interface ImportPreviewEntry {
  scope: MemoryScope;
  content: string;
}

interface MemoryServiceConfig {
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => string | undefined;
  organizationId: () => string | undefined;
}

export interface MemoryService {
  list(
    scope: MemoryScope,
    options?: { limit?: number; afterId?: string; beforeId?: string; query?: string }
  ): Promise<Result<PaginatedList<MemoryEntry>, Error>>;
  count(scope: MemoryScope, options?: { query?: string }): Promise<Result<number, Error>>;
  create(input: CreateMemoryInput): Promise<Result<MemoryEntry, Error>>;
  update(id: string, scope: MemoryScope, input: UpdateMemoryInput): Promise<Result<MemoryEntry, Error>>;
  delete(id: string, scope: MemoryScope): Promise<Result<{ success: boolean }, Error>>;
  importPreview(input: { text: string; scope: MemoryScope }): Promise<Result<{ entries: ImportPreviewEntry[] }, Error>>;
  importConfirm(
    input: { entries: ImportPreviewEntry[] },
    scope: MemoryScope
  ): Promise<Result<{ memories: MemoryEntry[] }, Error>>;
  pin(id: string, scope: MemoryScope): Promise<Result<MemoryEntry, Error>>;
  unpin(id: string, scope: MemoryScope): Promise<Result<MemoryEntry, Error>>;
  archive(id: string, scope: MemoryScope, reason: string): Promise<Result<MemoryEntry, Error>>;
  restore(id: string, scope: MemoryScope): Promise<Result<MemoryEntry, Error>>;
  listArchived(scope: MemoryScope): Promise<Result<{ memories: MemoryEntry[] }, Error>>;
  getPreferences(scope: MemoryScope): Promise<Result<{ scope: string; enabled: boolean }, Error>>;
  setPreferences(input: { scope: MemoryScope; enabled: boolean }): Promise<Result<{ success: boolean }, Error>>;
  compress(memoryIds: string[], scope: MemoryScope): Promise<Result<{ compressedText: string }, Error>>;
  compressConfirm(input: CompressConfirmInput): Promise<Result<MemoryEntry, Error>>;
  bulkDelete(memoryIds: string[], scope: MemoryScope): Promise<Result<{ success: boolean }, Error>>;
  health(options: {
    scope: MemoryScope;
    maxScore?: number;
    limit?: number;
  }): Promise<Result<{ memories: MemoryHealthEntry[] }, Error>>;
}

const scopePath = (scope: MemoryScope, suffix: string): string => {
  switch (scope) {
    case "user":
      return `/users${suffix}`;
    case "organization":
      return `/orgs${suffix}`;
    case "project":
      return `/projects${suffix}`;
  }
};

export function createMemoryService(config: MemoryServiceConfig): MemoryService {
  // Scope is in the URL path; the matching tenancy header (X-Project-Id /
  // X-Organization-Id) is still required for the middleware to resolve the tenant.
  const getHeaders = (scope?: MemoryScope) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = config.authToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    if (scope === "organization") {
      const orgId = config.organizationId();
      if (orgId) {
        headers["X-Organization-Id"] = orgId;
      }
    } else if (scope === "project") {
      const projectId = config.projectId();
      if (projectId) {
        headers["X-Project-Id"] = projectId;
      }
    }
    return headers;
  };

  const apiCall = async <T>(
    method: string,
    path: string,
    body?: unknown,
    scope?: MemoryScope
  ): Promise<Result<T, Error>> => {
    if (!config.authReady()) {
      return err(new Error("Auth not ready"));
    }

    try {
      const response = await fetch(`/api${path}`, {
        method,
        headers: getHeaders(scope),
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        return err(new Error(proxyErrorMessage(await response.json().catch(() => ({}))) ?? `HTTP ${response.status}`));
      }

      const data: unknown = await response.json();
      return ok(data as T);
    } catch (error) {
      return err(error instanceof Error ? error : new Error(String(error)));
    }
  };

  return {
    list: async (scope, options) => {
      const params = new URLSearchParams();
      if (options?.limit != null) params.set("limit", String(options.limit));
      if (options?.afterId != null) params.set("after_id", options.afterId);
      if (options?.beforeId != null) params.set("before_id", options.beforeId);
      if (options?.query) params.set("q", options.query);
      const query = params.toString();
      const base = scopePath(scope, "/memories");
      const result = await apiCall<{
        data: MemoryEntry[];
        first_id?: string;
        last_id?: string;
        has_more: boolean;
      }>("GET", query ? `${base}?${query}` : base, undefined, scope);
      if (result.isErr()) return err(result.error);
      return ok({
        data: result.value.data,
        firstId: result.value.first_id,
        lastId: result.value.last_id,
        hasMore: result.value.has_more,
      });
    },
    count: async (scope, options) => {
      const base = scopePath(scope, "/memories/count");
      const path = options?.query ? `${base}?${new URLSearchParams({ q: options.query })}` : base;
      const result = await apiCall<{ count: number }>("GET", path, undefined, scope);
      if (result.isErr()) return err(result.error);
      return ok(result.value.count);
    },
    create: (input) => {
      const { scope, ...body } = input;
      return apiCall<MemoryEntry>("POST", scopePath(scope, "/memories"), body, scope);
    },
    update: (id, scope, input) =>
      apiCall<MemoryEntry>("PUT", scopePath(scope, `/memories/${encodeURIComponent(id)}`), input, scope),
    delete: (id, scope) =>
      apiCall<{ success: boolean }>(
        "DELETE",
        scopePath(scope, `/memories/${encodeURIComponent(id)}`),
        undefined,
        scope
      ),
    importPreview: (input) => {
      const { scope, ...body } = input;
      return apiCall<{ entries: ImportPreviewEntry[] }>("POST", scopePath(scope, "/memories/import"), body, scope);
    },
    importConfirm: (input, scope) =>
      apiCall<{ memories: MemoryEntry[] }>("POST", scopePath(scope, "/memories/import/confirm"), input, scope),
    pin: (id, scope) =>
      apiCall<MemoryEntry>("POST", scopePath(scope, `/memories/${encodeURIComponent(id)}/pin`), {}, scope),
    unpin: (id, scope) =>
      apiCall<MemoryEntry>("DELETE", scopePath(scope, `/memories/${encodeURIComponent(id)}/pin`), undefined, scope),
    archive: (id, scope, reason) =>
      apiCall<MemoryEntry>("POST", scopePath(scope, `/memories/${encodeURIComponent(id)}/archive`), { reason }, scope),
    restore: (id, scope) =>
      apiCall<MemoryEntry>("POST", scopePath(scope, `/memories/${encodeURIComponent(id)}/restore`), undefined, scope),
    listArchived: (scope) =>
      apiCall<{ memories: MemoryEntry[] }>("GET", scopePath(scope, "/memories/archived"), undefined, scope),
    getPreferences: (scope) =>
      apiCall<{ scope: string; enabled: boolean }>("GET", scopePath(scope, "/memories/preferences"), undefined, scope),
    setPreferences: (input) => {
      const { scope, ...body } = input;
      return apiCall<{ success: boolean }>("POST", scopePath(scope, "/memories/preferences"), body, scope);
    },
    compress: (memoryIds, scope) =>
      apiCall<{ compressedText: string }>("POST", scopePath(scope, "/memories/compress"), { memoryIds }, scope),
    compressConfirm: (input) => {
      const { scope, ...body } = input;
      return apiCall<MemoryEntry>("POST", scopePath(scope, "/memories/compress/confirm"), body, scope);
    },
    bulkDelete: (memoryIds, scope) =>
      apiCall<{ success: boolean }>("POST", scopePath(scope, "/memories/delete/bulk"), { memoryIds }, scope),
    health: (options) => {
      const params = new URLSearchParams();
      if (options.maxScore !== undefined) params.set("maxScore", String(options.maxScore));
      if (options.limit !== undefined) params.set("limit", String(options.limit));
      const query = params.toString();
      const base = scopePath(options.scope, "/memories/health");
      return apiCall<{ memories: MemoryHealthEntry[] }>(
        "GET",
        query ? `${base}?${query}` : base,
        undefined,
        options.scope
      );
    },
  };
}
